import { ArrowDownTrayIcon, ArrowLeftIcon, ArrowPathIcon, Cog8ToothIcon, DocumentDuplicateIcon, EyeIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { LoaderSpinner } from "../../components/LoaderSpinner";
import { OverlayGrayBackground } from "../../components/OverlayGrayBackground";
import { TanStackTable } from "../../components/TanStackTable";
import { ROUTES } from "../../consts/general";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ConfrimModal } from "../../components/ConfrimModal";
import { ForecastPlanningSystemEditModal } from "../../components/quickbooks/ForecastPlanningSystemEditModal";
import { useEffect, useMemo, useRef, useState } from "react";
import { deletePlannedForecastRequest, duplicatePlannedForecastRequest, ForecastPlanningLoadingKeys, getActualsByYear, getPlannedForecasts, partialUpdateForecastPlanningRequest, updatePlannedForecastRequest } from "../../slices/quickbooks/forecastPlanningSlice";
import { arrayHasItems, formatPrice, getYearsFromToCurrent } from "../../helpers/common";
import { SelectDropdown } from "../../components/SelectDropdown";
import { AGGRESSIVE, BASE, GROWTH } from "../../consts/forecastPlanning/server";
import { SaveForecastPlanningSystemModal } from "../../components/planning/SaveForecastPlanningSystemModal";
import { defaultAllocation, monthsLowerCase } from "../../consts/forecast";
import { ForecastPlanningMonthlyAllocation } from "../../components/planning/ForecastPlanningMonthlyAllocation";
import { ActualsVSForecastHeader } from "../../components/planning/ActualsVSForecastHeader";
import { ActualsVSForecastTable } from "../../components/planning/ActualsVSForecastTable";
import { getGroupsBySelect } from "../../slices/productGroupSlice";
import { ChannelBreakdown } from "../../components/planning/ChannelBreakdown";
import { MonthlyBreakdown } from "../../components/planning/MonthlyBreakdown";
import { defaultActiveValuesOfActuals, defaultChannelsBreakdown, defaultForecastProductsSortOrder, defaultMonthlyBreakdown, forecastsOptions } from "../../consts/forecastPlanning/client";
import { TotalsSectionByForecastPlanningSystem } from "../../components/planning/TotalsSectionByForecastPlanningSystem";
import { GraphPerformance } from "../../components/planning/GraphPerformance";
import { DuplicateForecastSystemModal } from "../../components/planning/DuplicateForecastSystemModal";
import { exportAllPlanningTablesToCSV } from "../../helpers/planning/exports";
import { ForecastSystemSettingsSidebar } from "../../components/planning/ForecastSystemSettingsSidebar";
import { BudgetLineItemLoadingKeys, getBudgetLineItemsCards } from "../../slices/quickbooks/budgetLineItemSlice";
import ReactTooltip from "react-tooltip";
import { getBudgetForecastsOptions, getProfitAndLossNewData, getProfitAndLossTables, ProfitAndLossNewLoadingKeys } from "../../slices/quickbooks/profitAndLossNewSlice";
import { fetchForecastSettings } from "../../slices/forecastSettingsSlice";
import { getReportCategories } from "../../slices/quickbooks/reportCategories";

const getColoredBadge = (type) => {
    switch (type) {
        case (BASE): 
            return "bg-blue-500";
        case (GROWTH):
            return "bg-purple-500";
        case (AGGRESSIVE): 
            return "bg-orange-500"
        default:
            return null
    }
}

const planningMonthsShort = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const profitAndLossTableKeys = [
    "summary",
    "revenue-breakdown",
    "contribution-margin",
    "total-cost-of-goods-sold",
    "total-operating-expenses",
    "net-income-summary",
];

const getMonthMetricValue = (monthObj = {}, candidates = []) => {
    for (const candidate of candidates) {
        if (monthObj?.[candidate]?.value !== undefined) {
            return Number(monthObj[candidate].value || 0);
        }
    }

    const normalize = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
    const entries = Object.entries(monthObj || {});
    for (const candidate of candidates) {
        const foundEntry = entries.find(([key]) => normalize(key) === normalize(candidate));
        if (foundEntry?.[1]?.value !== undefined) {
            return Number(foundEntry[1].value || 0);
        }
    }

    return 0;
};

const getFallbackMetricSelection = ({ itemsById, candidates = [] }) => {
    const normalizedCandidates = (candidates || []).map((candidate) =>
        String(candidate || "").toLowerCase().replace(/[^a-z0-9]+/g, "")
    );

    return Object.values(itemsById || {}).find((item) => {
        const itemName = String(item?.item?.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
        return normalizedCandidates.includes(itemName);
    }) || null;
};

const ForecastPlanningSystem = ({
    access,
    marginFromSidebar
}) => {

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const pageParams = window?.location?.search ? new URLSearchParams(window?.location?.search) : null;
    const forecastPlanningId = pageParams?.get("id") ? Number(pageParams?.get("id")) : null;

    const [forecastPlanningIdToDelete, setForecastPlanningIdToDelete] = useState("");
    const [selectedForecastPlanning, setSelectedForecastPlanning] = useState(null);
    const [forecastPlanning, setForecastPlanning] = useState(null);
    const [duplicateForecastPlanning, setDuplicateForecastPlanning] = useState(null);
    const [deleteForecastPlanningModalIsOpen, setDeleteForecastPlanningModalIsOpen] = useState(false);
    const [saveForecastIsOpen, setSaveForecastIsOpen] = useState(false);
    const [forecastSettingsIsOpen, setForecastSettingsIsOpen] = useState(false);
    const [graphVariant, setGraphVariant] = useState("revenue");
    const [graphView, setGraphView] = useState("line");
    const [sortOrder, setSortOrder] = useState(defaultForecastProductsSortOrder);

    // forecast data
    const [selectedForecastType, setSelectedForecastType] = useState("");
    const [annualGoal, setAnnualGoal] = useState("");
    const [channelsBreakdown, setChannelsBreakdown] = useState(defaultChannelsBreakdown);
    const [selectedForecastYear, setSelectedForecastYear] = useState(null);
    const [monthlyAllocationData, setMonthlyAllocationData] = useState(defaultAllocation);
    const [activeActuals, setActiveActuals] = useState(defaultActiveValuesOfActuals);
    const [monthlyBreakdown, setMonthlyBreakdown] = useState(defaultMonthlyBreakdown);
    const [forecastProducts, setForecastProducts] = useState([]);

    const getSorter = (order) => {
        const map = {
            actuals_asc: (a, b) => Number(a?.annual_actual || 0) - Number(b?.annual_actual || 0),
            actuals_desc: (a, b) => Number(b?.annual_actual || 0) - Number(a?.annual_actual || 0),
            forecast_asc: (a, b) => Number(a?.annual_forecast || 0) - Number(b?.annual_forecast || 0),
            forecast_desc: (a, b) => Number(b?.annual_forecast || 0) - Number(a?.annual_forecast || 0),
            variance_asc: (a, b) => Number(a?.annual_variance || 0) - Number(b?.annual_variance || 0),
            variance_desc: (a, b) => Number(b?.annual_variance || 0) - Number(a?.annual_variance || 0),
            units_asc: (a, b) => Number(a?.annual_units || 0) - Number(b?.annual_units || 0),
            units_desc: (a, b) => Number(b?.annual_units || 0) - Number(a?.annual_units || 0),
            units_sold_asc: (a, b) => Number(a?.annual_units_sold || 0) - Number(b?.annual_units_sold || 0),
            units_sold_desc: (a, b) => Number(b?.annual_units_sold || 0) - Number(a?.annual_units_sold || 0),
            units_variance_asc: (a, b) => Number(a?.annual_units_variance || 0) - Number(b?.annual_units_variance || 0),
            units_variance_desc: (a, b) => Number(b?.annual_units_variance || 0) - Number(a?.annual_units_variance || 0),
        };
        return map[order] || map.actuals_desc;
    };

    const sortedForecastProducts = [...(forecastProducts || [])].sort(getSorter(sortOrder));

    // Persist in edited forecast data via updateForecastPlanningInformation flow
    const [editedForecastPlanningData, setEditedForecastPlanningData] = useState(null);
    const prevSelectedForecastTypeRef = useRef(null);
    const [currentBudget, setCurrentBudget] = useState(null);

    const {
        loading: forecastPlanningLoading,
        loadingKeys: forecastPlanningLoadingKeys,
        firstLoadComplete: forecastPlanningFirstLoadComplete,
        plannedForecasts,
        plannedActuals
    } = useSelector((state) => state.forecastPlanning);
    const {
      groupsBySelect,
    } = useSelector((state) => state.productGroup);
    const {
        budgetOptions,
        profitAndLossNewData: profitAndLossReport,
        profitAndLossTables,
        loading: budgetForecastLoading,
        loadingKeys: budgetForecastLoadingKeys,
    } = useSelector((state) => state.profitAndLossNew);
    const {
        loading: budgetLineItemLoading,
        loadingKeys: budgetLineItemLoadingKeys,
        budgetLineItemsTotals
    } = useSelector((state) => state.budgetLineItem);
    const { forecastSettings } = useSelector((state) => state.forecastSettings);

    const profitAndLossItemsById = useMemo(() => {
        return profitAndLossTableKeys.reduce((acc, tableKey) => {
            (profitAndLossTables?.[tableKey] || []).forEach((tableItem) => {
                if (!tableItem?.id) return;
                acc[tableItem.id] = tableItem;
            });
            return acc;
        }, {});
    }, [profitAndLossTables]);

    const metricSelections = useMemo(() => {
        const revenueSelection =
            profitAndLossItemsById?.[forecastSettings?.metrics?.revenue] ||
            getFallbackMetricSelection({
                itemsById: profitAndLossItemsById,
                candidates: ["Total Revenue", "Revenue"],
            });
        const contributionMarginSelection =
            profitAndLossItemsById?.[forecastSettings?.metrics?.contribution_margin] ||
            getFallbackMetricSelection({
                itemsById: profitAndLossItemsById,
                candidates: ["Contribution Margin"],
            });
        const netIncomeSelection =
            profitAndLossItemsById?.[forecastSettings?.metrics?.net_income] ||
            getFallbackMetricSelection({
                itemsById: profitAndLossItemsById,
                candidates: ["Net Income (expense)", "Net Income"],
            });

        return {
            revenue: revenueSelection,
            contribution_margin: contributionMarginSelection,
            net_income: netIncomeSelection,
        };
    }, [forecastSettings, profitAndLossItemsById]);

    const adSpendTotals =
        budgetLineItemsTotals?.categories?.[String(forecastSettings?.adSpend_category_id ?? 3)]?.months || {};
    const revenueTotals =
        budgetLineItemsTotals?.categories?.[String(forecastSettings?.revenue_category_id ?? 1)]?.months || {};
 
    const profitAndLossMonthlyMetrics = useMemo(() => {
        const yearSuffix = String(selectedForecastYear || new Date().getFullYear()).slice(2);
        return (monthsLowerCase || []).reduce((acc, monthKey, idx) => {
            const monthLabel = `${planningMonthsShort[idx]} ${yearSuffix}`;

            const getMetricValueBySelection = (selection) => {
                if (!selection?.table_key || !selection?.item?.name) return 0;
                const currentMonth = profitAndLossReport?.tableData?.[selection.table_key]?.[monthLabel] || {};
                return getMonthMetricValue(currentMonth, [selection.item.name]);
            };

            acc[monthKey] = {
                monthLabel,
                revenue: getMetricValueBySelection(metricSelections?.revenue),
                contributionMargin: getMetricValueBySelection(metricSelections?.contribution_margin),
                netIncome: getMetricValueBySelection(metricSelections?.net_income),
            };
            return acc;
        }, {});
    }, [metricSelections, profitAndLossReport, selectedForecastYear]);

    useEffect(() => {
        dispatch(getPlannedForecasts({}));
        dispatch(getGroupsBySelect({}));
        dispatch(getBudgetForecastsOptions({}));
        dispatch(fetchForecastSettings());
        dispatch(getReportCategories({}));
        dispatch(getProfitAndLossTables({}));
        ReactTooltip.rebuild();
    }, []);

    const getDescriptionInformation = () => {
        const year = selectedForecastYear;
        const forecast = forecastsOptions.find((forecast) => forecast.value === selectedForecastType)?.label || "-";
        const productsLength = (forecastProducts || []).length;
        const goal = formatPrice(Number(annualGoal || 0));
        return `Year: ${year} • Forecast: ${forecast} • Products: ${productsLength} • Goal: $${goal}`;
    };

    const getParamsForThePL = () => {
        const selectedYearNum = Number(selectedForecastYear);
        if (!Number.isFinite(selectedYearNum)) return null;

        const toYmdLocal = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return `${y}-${m}-${day}`;
        };

        const startDate = `${selectedYearNum}-01-01`;
        const endDate =
            selectedYearNum === new Date().getFullYear()
                ? toYmdLocal(new Date())
                : `${selectedYearNum}-12-31`;

        return { startDate, endDate };
    };

    useEffect(() => {
        if (selectedForecastYear) {
            const params = getParamsForThePL();
            if (!params) return;
            const { startDate, endDate } = params;
            dispatch(getProfitAndLossNewData({
                startDate,
                endDate,
            }));
            dispatch(getActualsByYear({
                year: selectedForecastYear
            }));
        }
    }, [
        selectedForecastYear,
    ]);

    useEffect(() => {
        if (currentBudget) {
            dispatch(getBudgetLineItemsCards({ id: currentBudget }));
        };
    }, [
        currentBudget
    ]);

    useEffect(() => {
        if (forecastPlanningId && arrayHasItems(plannedForecasts)) {
            setSelectedForecastPlanning(plannedForecasts.find((plannedForecast) => plannedForecast.id === forecastPlanningId));
        } else {
            clearAllValues();
            setSelectedForecastPlanning(null);
        }
    }, [
        forecastPlanningId,
        plannedForecasts
    ]);

    const getForecastsPlanningIsLoading = () => 
        forecastPlanningLoadingKeys.includes(ForecastPlanningLoadingKeys.getPlannedForecasts);
    const deleteForecastPlanningIsLoading = () =>
        forecastPlanningLoadingKeys.includes(ForecastPlanningLoadingKeys.deletePlannedForecast);

    const clearAllValues = () => {
        setEditedForecastPlanningData(null);
        setMonthlyAllocationData(defaultAllocation);
        setSelectedForecastYear(null);
        setActiveActuals(defaultActiveValuesOfActuals);
        setChannelsBreakdown(defaultChannelsBreakdown);
        setAnnualGoal("");
        setSelectedForecastType("");
        setCurrentBudget(null);
        setForecastProducts([]);
        setForecastSettingsIsOpen(false);
        prevSelectedForecastTypeRef.current = null;
    };

    const updateForecastPlanningInformation = () => {
        const selectedForecastPlanningData = selectedForecastPlanning?.data;
        const prevType = prevSelectedForecastTypeRef.current;
        const defaultYear = String(new Date().getFullYear());
        const defaultData = {
            annual_goal: 25000000,
            forecast_year: defaultYear,
            forecast_products: [],
            channels_breakdown: defaultChannelsBreakdown,
            monthly_breakdown: defaultMonthlyBreakdown,
            active_actuals: defaultActiveValuesOfActuals,
            forecast_products_sort_order: defaultForecastProductsSortOrder,
            monthly_allocation: {
                months: defaultAllocation,
            }
        };
        if (!selectedForecastPlanningData && !editedForecastPlanningData) {
            setSelectedForecastType(BASE);
            setForecastProducts([]);
            setSelectedForecastYear(defaultYear);
            setMonthlyAllocationData(defaultAllocation);
            setChannelsBreakdown(defaultChannelsBreakdown);
            setMonthlyBreakdown(defaultMonthlyBreakdown);
            setActiveActuals(defaultActiveValuesOfActuals);
            setSortOrder(defaultForecastProductsSortOrder);
            setAnnualGoal(defaultData.annual_goal);
            setEditedForecastPlanningData({
                [BASE]: defaultData,
                [GROWTH]: defaultData,
                [AGGRESSIVE]: defaultData
            });
            return;
        }

        if (selectedForecastPlanningData && !editedForecastPlanningData) {
            const lastSavedType = selectedForecastPlanning?.lastSavedType || BASE;
            const currentBudgetOption = selectedForecastPlanning?.budgetId || null;
            setSelectedForecastType(lastSavedType);
            setCurrentBudget(currentBudgetOption);
            setForecastProducts(selectedForecastPlanningData[lastSavedType]?.products || []);
            setSelectedForecastYear(selectedForecastPlanningData[lastSavedType]?.forecast_year || null);
            setChannelsBreakdown(selectedForecastPlanningData[lastSavedType]?.channels_breakdown || defaultChannelsBreakdown);
            setActiveActuals(selectedForecastPlanningData[lastSavedType]?.active_actuals || []);
            setMonthlyBreakdown(selectedForecastPlanningData[lastSavedType]?.monthly_breakdown || []);
            setSortOrder(selectedForecastPlanningData[lastSavedType]?.forecast_products_sort_order || defaultForecastProductsSortOrder);
            setMonthlyAllocationData(selectedForecastPlanningData[lastSavedType]?.monthly_allocation?.months || defaultAllocation);
            setAnnualGoal(selectedForecastPlanningData[lastSavedType]?.annual_goal);
            setEditedForecastPlanningData({
                [BASE]: selectedForecastPlanningData[BASE] || {},
                [GROWTH]: selectedForecastPlanningData[GROWTH] || {},
                [AGGRESSIVE]: selectedForecastPlanningData[AGGRESSIVE] || {}
            });
            return;
        }

        if (prevType !== selectedForecastType) {
            const prevEditedData = editedForecastPlanningData?.[selectedForecastType];
            setForecastProducts(prevEditedData.forecast_products || []);
            setSelectedForecastYear(prevEditedData.forecast_year || null);
            setActiveActuals(prevEditedData.active_actuals || []);
            setMonthlyBreakdown(prevEditedData?.monthly_breakdown || defaultMonthlyBreakdown);
            setChannelsBreakdown(prevEditedData?.channels_breakdown || defaultChannelsBreakdown);
            setSortOrder(prevEditedData?.forecast_products_sort_order || defaultForecastProductsSortOrder);
            setMonthlyAllocationData(prevEditedData.monthly_allocation?.months || defaultAllocation);
            setAnnualGoal(prevEditedData?.annual_goal);
            prevSelectedForecastTypeRef.current = selectedForecastType;
            return;
        }

        const nextMonthly = (monthsLowerCase || []).reduce((acc, m, idx) => {
            const forecast = (forecastProducts || []).reduce((sum, p) => sum + Number(p?.monthly_forecast_by_month?.[m] || 0), 0);
            const actual = (forecastProducts || []).reduce((sum, p) => sum + Number(p?.monthly_actuals_by_month?.[m] || 0), 0);
            const variance = forecast > 0 ? ((actual / forecast) * 100) : 0;
            const cmForecast = (Number(revenueTotals?.[m] || 0) / 1.3) - Number(adSpendTotals?.[m] || 0);
            const cmActual = Number(profitAndLossMonthlyMetrics?.[m]?.contributionMargin || 0);
            const cmVariance = cmForecast > 0 ? ((cmActual / cmForecast) * 100) : 0;
            acc[m] = {
                forecast,
                actual,
                variance,
                cmForecast,
                cmActual,
                cmVariance,
            };
            return acc;
        }, {});
        setMonthlyBreakdown(nextMonthly);
        setCurrentBudget(currentBudget);

        setEditedForecastPlanningData(prev => ({
            ...prev,
            [selectedForecastType]: {
                ...(prev?.[selectedForecastType] || {}),
                annual_goal: annualGoal,
                forecast_year: selectedForecastYear,
                forecast_products: forecastProducts,
                channels_breakdown: channelsBreakdown,
                monthly_breakdown: nextMonthly,
                forecast_products_sort_order: sortOrder,
                active_actuals: activeActuals,
                monthly_allocation: {
                    months: monthlyAllocationData,
                }
            }
        }));
        prevSelectedForecastTypeRef.current = selectedForecastType;
    };

    useEffect(() => {
        if (
            !forecastPlanningFirstLoadComplete || 
            !selectedForecastPlanning ||
            !forecastPlanningId ||
            getForecastsPlanningIsLoading() ||
            profitAndLossReportIsLoading()
        ) {
            return;
        };
        updateForecastPlanningInformation();
    }, [
        forecastPlanningId,
        selectedForecastType,
        monthlyAllocationData,
        currentBudget,
        channelsBreakdown,
        adSpendTotals,
        profitAndLossReport,
        profitAndLossMonthlyMetrics,
        forecastProducts,
        revenueTotals,
        sortOrder,
        activeActuals,
        annualGoal,
        selectedForecastYear,
        selectedForecastPlanning
    ]);

    const getBudgetLineItemsIsLoading = () =>
        budgetLineItemLoadingKeys.includes(BudgetLineItemLoadingKeys.getBudgetLineItems);
    const profitAndLossReportIsLoading = () =>
        budgetForecastLoadingKeys.includes(ProfitAndLossNewLoadingKeys.getProfitAndLossNewData);
    const budgetForecastOptionsIsLoading = () =>
        budgetForecastLoadingKeys.includes(ProfitAndLossNewLoadingKeys.getBudgetForecastsOptions);

    return (
        <>
            {(profitAndLossReportIsLoading() || getBudgetLineItemsIsLoading() || getForecastsPlanningIsLoading()) && (
                <div className="fixed inset-0 z-[200] bg-gray-400/50 flex items-center justify-center">
                    <LoaderSpinner width="110" height="110" border="8" colorSpinner="black" />
                </div>
            )}
            <main className="w-full h-auto px-4 sm:px-6 lg:px-8 mb-10">
                {!selectedForecastPlanning && (
                    <div className="h-auto py-6 pb-0 flex flex-col">
                        <div className="flex items-center mb-8 justify-between gap-4 flex-wrap">
                            <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-2">
                                    <h1 className="text-2xl font-semibold text-gray-900">Forecast Planning System Snapshots</h1>
                                </div>
                            </div>
                            {access === "edit" && (
                                <div className="flex flex-row gap-1">
                                    <div className="flex flex-row items-center gap-2 flex-wrap">
                                        <button
                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                            onClick={() => {
                                                setForecastPlanning({});
                                            }}
                                        >
                                            <span>Create Plan</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {!forecastPlanningFirstLoadComplete ? (
                    <LoaderSpinner width="70" height="70" border="5" />
                ) : selectedForecastPlanning ? (
                    <section className="border my-8 rounded-lg relative">
                        {getForecastsPlanningIsLoading() && <OverlayGrayBackground />}
                        <div className="border-b bg-white px-6 py-4 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div className="flex flex-row gap-4 items-center">
                                    <div>
                                        <h1 className="text-xl font-semibold">
                                            Forecast Planning System
                                        </h1>
                                    </div>
                                    <div 
                                        className="flex flex-row items-center shrink-0 h-max gap-2 bg-primary text-white border-blue-200 px-4 py-0.5 rounded-full font-bold text-sm whitespace-nowrap cursor-pointer"
                                        onClick={() => {
                                            navigate(`${ROUTES.FORECAST_PLANNING_SYSTEM}`);
                                            clearAllValues();
                                        }}
                                    >
                                        <ArrowLeftIcon className="h-5 w-5" />
                                        Return
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={`sticky top-0 ${forecastSettingsIsOpen ? "z-10" : "z-[100]"} bg-background/95 backdrop-blur border-b`}>
                            <div className="flex items-center gap-3 px-6 py-3 min-h-[64px]">
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm font-medium whitespace-nowrap">
                                            Forecast
                                        </label>
                                        <div className="w-48">
                                            <SelectDropdown
                                                options={forecastsOptions}
                                                value={selectedForecastType}
                                                onChange={setSelectedForecastType}
                                                selectClassname="bg-white !px-3 !py-1.5"
                                                dropdownClassname="!z-[150]"
                                            />
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setSaveForecastIsOpen(true);
                                        }}
                                        className="gap-2 whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 shrink-0 outline-none shadow-xs inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 h-9"
                                    >
                                        Save
                                    </button>
                                    <button
                                        onClick={() => {
                                            setForecastSettingsIsOpen(true);
                                        }}
                                        className="gap-2 whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 shrink-0 outline-none shadow-xs inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 h-9"
                                    >
                                        <Cog8ToothIcon className="h-5 w-5" />
                                    </button>
                                </div>
                                <div className="h-6 w-px bg-border" />
                                <div className="flex items-center gap-3 relative z-0">
                                    <div className="flex flex-col">
                                        <div className="flex items-center gap-2">
                                            <label className="text-sm font-medium whitespace-nowrap">
                                                Annual Goal
                                            </label>
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-sm">
                                                    $
                                                </span>
                                                <input
                                                    value={annualGoal}
                                                    onChange={(e) => setAnnualGoal(e.target.value)}
                                                    onBlur={async () => {
                                                        if (!forecastPlanningId) return;
                                                        await dispatch(partialUpdateForecastPlanningRequest({
                                                            forecastPlanningId: forecastPlanningId,
                                                            lastType: selectedForecastType || 'BASE',
                                                            scenarioChanges: { annual_goal: Number(annualGoal || 0) },
                                                        }));
                                                    }}
                                                    className="border-input flex min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm w-[200px] h-9 pl-9 text-right"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm font-medium whitespace-nowrap">
                                            Year
                                        </label>
                                        <div className="w-24">
                                            <SelectDropdown
                                                options={[...getYearsFromToCurrent(2024)]}
                                                value={selectedForecastYear}
                                                onChange={async (val) => {
                                                    setSelectedForecastYear(val);
                                                    if (!forecastPlanningId) return;
                                                    await dispatch(partialUpdateForecastPlanningRequest({
                                                        forecastPlanningId: forecastPlanningId,
                                                        lastType: selectedForecastType || 'BASE',
                                                        scenarioChanges: { forecast_year: val },
                                                    }));
                                                }}
                                                selectClassname="bg-white !px-3 !py-1.5"
                                                dropdownClassname="!z-[150]"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-row gap-2 items-center">
                                        <span>Budget:</span>
                                        <div className="w-48">
                                            <SelectDropdown
                                                options={[{ label: "No Budget", value: null }, ...budgetOptions]}
                                                value={currentBudget}
                                                onChange={async (val) => {
                                                    setCurrentBudget(val);
                                                    if (!forecastPlanningId) return;
                                                    await dispatch(partialUpdateForecastPlanningRequest({
                                                        forecastPlanningId: forecastPlanningId,
                                                        topLevelChanges: { budget_id: val || null },
                                                    }));
                                                }}
                                                disabled={budgetForecastOptionsIsLoading()}
                                                placeholder=""
                                                selectClassname="bg-white !px-3 !py-1.5"
                                                dropdownClassname="!z-[150]"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-row gap-2 items-center">
                                        <button 
                                        onClick={() => {
                                            exportAllPlanningTablesToCSV({
                                                selectedForecastYear,
                                                forecastProducts: sortedForecastProducts,
                                                groupsBySelect,
                                                channelsBreakdown,
                                                mix: forecastProducts,
                                                sortOrder,
                                            });
                                        }} 
                                            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 shrink-0 outline-none border bg-white shadow-xs h-8 rounded-xl gap-1.5 p-4 hover:bg-black hover:text-white"
                                        >
                                            <ArrowDownTrayIcon className="h-5 w-5" />
                                            <span>Export CSV</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <TotalsSectionByForecastPlanningSystem 
                            forecastProducts={forecastProducts || []}
                            selectedForecastYear={selectedForecastYear}
                        />
                        <div className="flex flex-col gap-6 p-6">
                            <div className="bg-[#f5f5f5] flex flex-col gap-3 rounded-xl border py-6 shadow-sm">
                                <ActualsVSForecastHeader
                                    selectedForecastYear={selectedForecastYear}
                                    forecastProducts={sortedForecastProducts}
                                    activeActuals={activeActuals}
                                    groupsBySelect={groupsBySelect}
                                    setActiveActuals={setActiveActuals}
                                    sortOrder={sortOrder}
                                    setSortOrder={setSortOrder}
                                    onChangeSortOrder={(val) => {
                                        setEditedForecastPlanningData(prev => ({
                                            ...prev,
                                            [selectedForecastType]: {
                                                ...(prev?.[selectedForecastType] || {}),
                                                forecast_products_sort_order: val,
                                            }
                                        }));
                                    }}
                                />
                                <ActualsVSForecastTable
                                    key={selectedForecastType}
                                    activeActuals={activeActuals}
                                    selectedForecastYear={selectedForecastYear}
                                    summaryActuals={profitAndLossReport}
                                    plannedActuals={plannedActuals}
                                    forecastProducts={sortedForecastProducts}
                                    annualGoal={annualGoal}
                                    monthlyAllocationData={monthlyAllocationData}
                                    setForecastProducts={setForecastProducts}
                                    groupsBySelect={groupsBySelect}
                                />
                            </div>
                            <ChannelBreakdown 
                                channelsBreakdown={channelsBreakdown}
                                forecastPlanningId={forecastPlanningId}
                                lastType={selectedForecastType || 'BASE'}
                                setChannelsBreakdown={setChannelsBreakdown}
                                groupsBySelect={groupsBySelect}
                                mix={forecastProducts || []}
                            />
                            <MonthlyBreakdown 
                                selectedForecastYear={selectedForecastYear}
                                forecastProducts={forecastProducts}
                                profitAndLossMonthlyMetrics={profitAndLossMonthlyMetrics}
                                adSpendTotals={adSpendTotals}
                                revenueTotals={revenueTotals}
                            />
                            <ForecastPlanningMonthlyAllocation
                                monthsData={monthlyAllocationData}
                                annualGoal={annualGoal}
                                forecastPlanningId={forecastPlanningId}
                                lastType={selectedForecastType || 'BASE'}
                                access={access}
                                onSave={(newData) => setMonthlyAllocationData(newData)}
                            />
                            <GraphPerformance
                                forecastProducts={forecastProducts || []}
                                graphVariant={graphVariant}
                                selectedForecastYear={selectedForecastYear}
                                setGraphVariant={setGraphVariant}
                                graphView={graphView}
                                setGraphView={setGraphView}
                                profitAndLossMonthlyMetrics={profitAndLossMonthlyMetrics}
                                adSpendTotals={adSpendTotals}
                                revenueTotals={revenueTotals}
                            />
                        </div>
                    </section>
                ) : (
                    <div className="w-full flex flex-col gap-4">
                        <section className="flex flex-col gap-2">
                            <div className="relative">
                                {getForecastsPlanningIsLoading() && <OverlayGrayBackground />}
                                <div className="w-full">
                                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                                        <TanStackTable
                                            data={plannedForecasts || []}
                                            hasActions={true}
                                            onRowClick={(row) => {
                                                navigate(`${ROUTES.FORECAST_PLANNING_SYSTEM}?id=${row.original.id}`);
                                            }}
                                            columns={[
                                                { accessorKey: "id", header: "ID", enableSorting: false, headerTrClassName: "w-[60px]" },
                                                { accessorKey: "name", header: "Name", enableSorting: false, headerTrClassName: "min-w-[260px]" },
                                                { accessorKey: "note", header: "Note", enableSorting: false, headerTrClassName: "min-w-[260px]" },
                                                { accessorKey: "lastSavedType", header: "Forecast", enableSorting: false, headerTrClassName: "min-w-[260px]", cell: ({ row }) => { 
                                                    return (
                                                        row.original.lastSavedType ? (
                                                            <span
                                                                className={`inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 gap-1 overflow-hidden text-white ${getColoredBadge(row.original.lastSavedType)}`}
                                                            >
                                                                {row.original.lastSavedType}
                                                            </span>
                                                        ) : null
                                                    )
                                                }},
                                                { accessorKey: "createdAtStr", header: "Created At", enableSorting: false, headerTrClassName: "min-w-[260px]"},
                                                { accessorKey: "updatedAtStr", header: "Updated At", enableSorting: false, headerTrClassName: "min-w-[260px]"},
                                                access === "edit" ? {
                                                    accessorKey: "actions", headerTrClassName: "min-w-[80px]", headerClassName: "justify-end", header: "Actions", enableSorting: false, cell: ({ row }) => {
                                                        return (
                                                            <div className={`flex gap-2 justify-end ${row.index % 2 === 0 ? '!bg-white' : '!bg-gray-50'}`}>
                                                                {access === "edit" && (
                                                                    <>
                                                                        <button
                                                                            className="inline-flex items-center px-3 py-1.5 border border-transparent rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"      
                                                                        >
                                                                            <EyeIcon className="h-4 w-4" />
                                                                        </button>
                                                                        <button
                                                                            className="inline-flex items-center px-3 py-1.5 border border-transparent rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"  
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setDuplicateForecastPlanning(row.original);
                                                                            }} 
                                                                        >
                                                                            <DocumentDuplicateIcon className="h-4 w-4" />
                                                                        </button>
                                                                        <button
                                                                            className="inline-flex items-center px-3 py-1.5 border border-transparent rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"  
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setForecastPlanning(row.original);
                                                                            }} 
                                                                        >
                                                                            <PencilIcon className="h-4 w-4" />
                                                                        </button>
                                                                        <button
                                                                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 rounded border border-black disabled:opacity-50"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setForecastPlanningIdToDelete(row.original.id);
                                                                                setDeleteForecastPlanningModalIsOpen(true);
                                                                            }}
                                                                        >
                                                                            {(deleteForecastPlanningIsLoading() && forecastPlanningIdToDelete === row.original.id) ? (
                                                                                <ArrowPathIcon className="animate-spin h-4 w-4" />
                                                                            ) : (
                                                                                <TrashIcon className="h-4 w-4" aria-hidden="true" />
                                                                            )}
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        )
                                                    }
                                                } : null,
                                            ].filter(Boolean)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                )}
            </main>
            <ForecastPlanningSystemEditModal
                key={`${forecastPlanning?.name}_${forecastPlanning?.id}`}
                isOpen={access === "edit" && !!forecastPlanning}
                onClose={() => {
                    setForecastPlanning(null);
                }}
                labelItem={forecastPlanning?.name}
                noteItem={forecastPlanning?.note}
                onAccept={async ({ name, note }) => {
                    await dispatch(updatePlannedForecastRequest({ forecastPlanningId: forecastPlanning?.id, name, note }));
                    setForecastPlanning(null);
                    dispatch(getPlannedForecasts({}));
                }}
            />
            <DuplicateForecastSystemModal
                isOpen={access === "edit" && !!duplicateForecastPlanning}
                onClose={() => {
                    setDuplicateForecastPlanning(null);
                }}
                forecastName={duplicateForecastPlanning?.name || ""}
                onAccept={async ({ name, options }) => {
                    await dispatch(duplicatePlannedForecastRequest({ forecastPlanningId: duplicateForecastPlanning?.id, name, options }));
                    setDuplicateForecastPlanning(null);
                    dispatch(getPlannedForecasts({}));
                }}
            />
            <SaveForecastPlanningSystemModal
                isOpen={saveForecastIsOpen}
                onClose={() => {
                    setSaveForecastIsOpen(false);
                }}
                labelItem={selectedForecastPlanning?.name}
                noteItem={selectedForecastPlanning?.note}
                descriptionInformation={getDescriptionInformation()}
                onAccept={async ({ name, note, actualPlans }) => {
                    await dispatch(updatePlannedForecastRequest({ 
                        forecastPlanningId: selectedForecastPlanning?.id, 
                        budgetId: currentBudget,
                        name, 
                        note, 
                        actualPlans,
                        lastType: selectedForecastType,
                        fromSave: true,
                        editedData: editedForecastPlanningData
                    }));
                    setSaveForecastIsOpen(false);
                    dispatch(getPlannedForecasts({}));
                }}
            />
            <ConfrimModal
                isOpen={deleteForecastPlanningModalIsOpen && access === "edit"}
                title={
                    `Are you sure want to delete this forecast?`
                }
                isLoading={deleteForecastPlanningIsLoading()}
                confirmButtonTitle={"Delete"}
                submitClick={async () => {
                    await dispatch(deletePlannedForecastRequest({ id: forecastPlanningIdToDelete }));
                    setForecastPlanningIdToDelete("");
                    setDeleteForecastPlanningModalIsOpen(false);
                    dispatch(getPlannedForecasts({}));
                }}
                cancelClick={() => {
                    setForecastPlanningIdToDelete("");
                    setDeleteForecastPlanningModalIsOpen(false);
                }}
            />
            <ForecastSystemSettingsSidebar
                isOpen={forecastSettingsIsOpen}
                onClose={() => setForecastSettingsIsOpen(false)}
                marginFromSidebar={marginFromSidebar}
            />
        </>
    )
}

export default ForecastPlanningSystem;